import React from "react";
import { useTranslation } from "react-i18next";
import { getSettings } from "src/services/configService";
import { RootState, useDispatch, useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { getDeviceUptime } from "@dashboard/devices/utils/device";
import { getDevicePolicyStatus, getPolicyIconColor, policyStatusForTabs } from "@dashboard/devices/utils/license";
import { selectGroupById } from "@dashboard/groups/store/selectors";
import { setPaneTab, showDetails } from "@devices/store";
import { DeviceState, License, PolicyStatus } from "@devices/types";
import { Box, Chip, Theme, Typography, useTheme } from "@mui/material";
import TimeService from "@services/timeService/timeService";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "@sidePanel/shared/Property";

type Props = {
    deviceState: DeviceState;
    license: License;
};

const emteriaServices = ["kiosk", "mdm", "sshd", "vncd", "vncr"] as const;

const useStyles = makeStyles()((theme: Theme) => ({
    iconWithText: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    policyCommandList: {
        listStyle: "none",
        margin: 0,
        paddingLeft: 0,
    },
    policyCommandNumber: {
        marginRight: theme.spacing(1),
    },
    policyCommandFailed: {
        color: theme.palette.emteria.error,
    },
    policyCommandError: {
        color: theme.palette.emteria.error,
        marginTop: theme.spacing(0.5),
    },
    link: {
        color: theme.palette.emteria.link,
        cursor: "pointer",
        "&:hover": {
            textDecoration: "underline",
        },
    },
}));

const SystemTab = ({ deviceState, license }: Props): React.JSX.Element => {
    const { version, properties, policy, locale, services, datetime } = deviceState || {};
    const { t } = useTranslation(["common", "sidepanels"]);
    const { classes } = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { features } = getSettings();
    const emteriaServicesEnabled = features.licensePanelSections.emteriaServicesEnabled;
    const groupPolicyTabAvailable = features.groupPanelTabs.policyEnabled;

    const timezone = properties?.["persist.sys.timezone"];
    const formattedDateTime = TimeService.getWithTimeZone(datetime.currentMs, timezone);
    const policyReceivedDate = policy?.received ? TimeService.getLocalDate(policy.received, "SECONDS", "dateTime") : "-";
    const uptime = getDeviceUptime(license.device);

    const group = useSelector((state: RootState) => selectGroupById(state, license.groupId));
    const policyStatusRaw = getDevicePolicyStatus(license.device, group);
    const policyStatus = policyStatusForTabs(policyStatusRaw);
    const policyIconColor = getPolicyIconColor(policyStatusRaw, theme);

    const policyCommands = group?.policy ?? [];
    const policyErrors = policy?.errors ?? {};
    const showErrors = policyStatusRaw === PolicyStatus.NON_COMPLIANT;
    const isEmptyPolicy = policyStatusRaw === PolicyStatus.NO_GROUP_POLICY;
    const openGroupPolicy = () => {
        dispatch(setPaneTab("POLICY"));
        dispatch(showDetails({ type: "group", selectedId: license.groupId }));
    };

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.osTab.version")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.androidVersion")}</PropertyKey>
                    <PropertyValue>{version?.androidVersion ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.buildNumber")}</PropertyKey>
                    <PropertyValue>{properties?.["ro.build.display.id"] ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.buildVersion")}</PropertyKey>
                    <PropertyValue>{version?.emteriaVersion ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.androidTimestamp")}</PropertyKey>
                    <PropertyValue>
                        {version?.androidTimestamp
                            ? TimeService.getLocalDate(version.androidTimestamp, "SECONDS", "dateTime")
                            : "-"}
                    </PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.osTab.policyState")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.lastChange")}</PropertyKey>
                    <PropertyValue>{policyReceivedDate}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("common:label.status")}</PropertyKey>
                    <PropertyValue>
                        <Box className={classes.iconWithText}>
                            <i className="fa-solid fa-gears" style={{ color: policyIconColor }} />
                            <span>{policyStatus ?? "-"}</span>
                        </Box>
                    </PropertyValue>
                </PropertyRow>
                {policyCommands.length > 0 ? (
                    <PropertyRow style={{ alignItems: "flex-start" }}>
                        <PropertyKey>{t("sidepanels:devicePanel.osTab.commands")}</PropertyKey>
                        <PropertyValue>
                            <Box component="ol" className={classes.policyCommandList}>
                                {policyCommands.map((command, index) => {
                                    const caption = command.caption || command.command;
                                    const errorMessage = showErrors ? policyErrors[String(index)] : undefined;
                                    return (
                                        <Box component="li" key={index}>
                                            <Typography
                                                component="div"
                                                variant="body1"
                                                className={errorMessage ? classes.policyCommandFailed : undefined}
                                            >
                                                <span className={classes.policyCommandNumber}>#{index + 1}</span>
                                                {caption}
                                            </Typography>
                                            {errorMessage ? (
                                                <Typography
                                                    component="div"
                                                    variant="caption"
                                                    className={classes.policyCommandError}
                                                >
                                                    {errorMessage}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </PropertyValue>
                    </PropertyRow>
                ) : null}
                {groupPolicyTabAvailable && !isEmptyPolicy ? (
                    <PropertyRow>
                        <PropertyKey>{t("sidepanels:devicePanel.osTab.policy")}</PropertyKey>
                        <PropertyValue>
                            <span className={classes.link} onClick={openGroupPolicy}>
                                {t("sidepanels:devicePanel.osTab.viewGroupPolicy")}
                            </span>
                        </PropertyValue>
                    </PropertyRow>
                ) : null}
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.osTab.systemTime")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.dateTime")}</PropertyKey>
                    <PropertyValue>{formattedDateTime}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.timezone")}</PropertyKey>
                    <PropertyValue>{timezone}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.osTab.system")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.type")}</PropertyKey>
                    <PropertyValue>{deviceState?.build?.type ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.uptime")}</PropertyKey>
                    <PropertyValue>{uptime}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:devicePanel.osTab.locale")}</PaneElement.Header>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.localeLanguage")}</PropertyKey>
                    <PropertyValue>{locale?.localeLanguage ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.localeCountry")}</PropertyKey>
                    <PropertyValue>{locale?.localeCountry ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("common:label.displayName")}</PropertyKey>
                    <PropertyValue>{locale?.displayName ?? "-"}</PropertyValue>
                </PropertyRow>
                <PropertyRow>
                    <PropertyKey>{t("sidepanels:devicePanel.osTab.displayCountry")}</PropertyKey>
                    <PropertyValue>{locale?.displayCountry ?? "-"}</PropertyValue>
                </PropertyRow>
            </PaneElement.Section>

            {emteriaServicesEnabled ? (
                <PaneElement.Section withoutBorder>
                    <PaneElement.Header>{t("sidepanels:devicePanel.osTab.emteriaServices")}</PaneElement.Header>
                    {emteriaServices.map((service) => (
                        <PropertyRow key={service}>
                            <PropertyKey>{service.toUpperCase()}</PropertyKey>
                            <PropertyValue>
                                <Chip
                                    label={
                                        services?.emteria?.[service]?.enabled
                                            ? t("common:label.enabled")
                                            : t("common:label.disabled")
                                    }
                                    color={services?.emteria?.[service]?.enabled ? "success" : "default"}
                                    size="small"
                                    sx={{ mb: 1 }}
                                    clickable={false}
                                    onClick={() => {}}
                                />
                            </PropertyValue>
                        </PropertyRow>
                    ))}
                </PaneElement.Section>
            ) : null}
        </PaneElement.Container>
    );
};

export default SystemTab;
